import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Bell, Plus, Trash2, Send, Power, PowerOff, X as XIcon, AlertTriangle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API = `${window.__BACKEND_URL__}/api`;

const ALERT_TYPES = [
  { value: 'overdue_jobs_count', label: 'Overdue Jobs Threshold',
    description: 'Fires when overdue job count ≥ your threshold.',
    defaults: { threshold: 5 } },
  { value: 'kpi_threshold', label: 'KPI Threshold',
    description: 'Fires when a selected KPI crosses a value.',
    defaults: { metric: 'completion_rate', operator: '<', value: 80, date_range_mode: 'this_month' } },
  { value: 'no_clock_in_by_time', label: 'Not Clocked In By Time',
    description: 'Fires if a worker/team hasn\'t clocked in by the cutoff time.',
    defaults: { cutoff_time: '09:00' } },
  { value: 'attendance_absent', label: 'Attendance Absent',
    description: 'Fires mid-day if anyone in scope is still absent.',
    defaults: { cutoff_time: '11:00' } },
  { value: 'job_over_budget', label: 'Job Over Budget',
    description: 'Fires when an active job exceeds N% of its scheduled window.',
    defaults: { percent_threshold: 80 } },
];

const KPI_METRICS = [
  { value: 'completion_rate', label: 'Completion Rate (%)' },
  { value: 'overdue_rate', label: 'Overdue Rate (%)' },
  { value: 'avg_completion_hours', label: 'Avg Completion Hours' },
];

export default function SmartAlerts({ token, user }) {
  const { toast } = useToast();
  const [alerts, setAlerts] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, t] = await Promise.all([
        axios.get(`${API}/smart-alerts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/teams`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
      ]);
      setAlerts(a.data || []);
      setTeams(t.data || []);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (a) => { setEditing(a); setModalOpen(true); };

  const testAlert = async (id) => {
    try {
      const r = await axios.post(`${API}/smart-alerts/${id}/test`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Test run complete', description: `Status: ${r.data.result.status}` });
      load();
    } catch (e) {
      toast({ title: 'Test failed', description: e.response?.data?.detail || 'Error', variant: 'destructive' });
    }
  };

  const toggleActive = async (a) => {
    await axios.put(`${API}/smart-alerts/${a.id}`, {
      name: a.name, description: a.description, alert_type: a.alert_type,
      config: a.config || {}, recipients: a.recipients, team_id: a.team_id,
      throttle_minutes: a.throttle_minutes, active: !a.active,
    }, { headers: { Authorization: `Bearer ${token}` } });
    load();
  };

  const del = async (id) => {
    if (!window.confirm('Delete this smart alert?')) return;
    await axios.delete(`${API}/smart-alerts/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    load();
  };

  return (
    <Card className="shadow-lg" data-testid="smart-alerts-section">
      <CardHeader className="bg-[#1A2433] border-b flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-bold flex items-center gap-2">
          <Bell className="w-5 h-5" /> Smart Alerts
        </CardTitle>
        <Button onClick={openNew} data-testid="new-smart-alert-btn" className="bg-[#1E88E5] hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-1" /> New Alert
        </Button>
      </CardHeader>
      <CardContent className="p-4">
        {loading ? (
          <p className="text-slate-400 text-sm">Loading…</p>
        ) : alerts.length === 0 ? (
          <div className="text-center py-8">
            <AlertTriangle className="w-10 h-10 mx-auto mb-3" style={{ color: '#64748B' }} />
            <p className="text-slate-400 text-sm mb-2">No smart alerts configured.</p>
            <p className="text-xs text-slate-500">
              Create rules to proactively monitor overdue jobs, KPIs, attendance, or budget overruns.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid #2D3748' }}>
                  <th className="text-left px-2 py-2 text-xs text-slate-400">Name</th>
                  <th className="text-left px-2 py-2 text-xs text-slate-400">Type</th>
                  <th className="text-left px-2 py-2 text-xs text-slate-400">Recipients</th>
                  <th className="text-left px-2 py-2 text-xs text-slate-400">Last Status</th>
                  <th className="text-right px-2 py-2 text-xs text-slate-400"></th>
                </tr>
              </thead>
              <tbody>
                {alerts.map((a) => {
                  const typeMeta = ALERT_TYPES.find((t) => t.value === a.alert_type);
                  return (
                    <tr key={a.id} style={{ borderBottom: '1px solid #1A2433' }}
                        data-testid={`alert-row-${a.id}`}>
                      <td className="px-2 py-2 text-white">
                        <button onClick={() => openEdit(a)} className="underline hover:text-[#1E88E5]">{a.name}</button>
                        {a.description && <div className="text-[11px] text-slate-500">{a.description}</div>}
                      </td>
                      <td className="px-2 py-2 text-xs" style={{ color: '#CBD5E1' }}>{typeMeta?.label || a.alert_type}</td>
                      <td className="px-2 py-2 text-xs" style={{ color: '#A0AEC0' }}>{(a.recipients || []).join(', ')}</td>
                      <td className="px-2 py-2 text-xs">
                        <span style={{
                          color: a.last_status === 'fired' ? '#FFA000' :
                                  a.last_status === 'error' ? '#EF4444' :
                                  a.last_status === 'throttled' ? '#64748B' : '#00C896',
                        }}>
                          {a.last_status || (a.active ? 'ready' : 'paused')}
                        </span>
                        {a.last_fired_at && (
                          <div className="text-[10px]" style={{ color: '#64748B' }}>
                            {new Date(a.last_fired_at).toLocaleString()}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right whitespace-nowrap">
                        <button onClick={() => testAlert(a.id)}
                                className="p-1.5 rounded mr-1"
                                style={{ background: 'rgba(0,200,150,0.15)', color: '#00C896' }}
                                title="Test now" data-testid={`test-alert-${a.id}`}>
                          <Send className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => toggleActive(a)}
                                className="p-1.5 rounded mr-1"
                                style={{
                                  background: a.active ? 'rgba(30,136,229,0.15)' : 'rgba(100,116,139,0.15)',
                                  color: a.active ? '#1E88E5' : '#94A3B8'
                                }}
                                title={a.active ? 'Pause' : 'Activate'}
                                data-testid={`toggle-alert-${a.id}`}>
                          {a.active ? <Power className="w-3.5 h-3.5" /> : <PowerOff className="w-3.5 h-3.5" />}
                        </button>
                        <button onClick={() => del(a.id)}
                                className="p-1.5 rounded"
                                style={{ background: 'rgba(239,68,68,0.15)', color: '#EF4444' }}
                                title="Delete" data-testid={`delete-alert-${a.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      {modalOpen && (
        <AlertEditor
          token={token}
          initial={editing}
          teams={teams}
          defaultRecipient={user?.email}
          onClose={() => setModalOpen(false)}
          onSaved={() => { setModalOpen(false); load(); }}
        />
      )}
    </Card>
  );
}

function AlertEditor({ token, initial, teams, defaultRecipient, onClose, onSaved }) {
  const isEdit = !!initial;
  const [name, setName] = useState(initial?.name || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [alertType, setAlertType] = useState(initial?.alert_type || 'overdue_jobs_count');
  const [config, setConfig] = useState(initial?.config || ALERT_TYPES[0].defaults);
  const [recipients, setRecipients] = useState((initial?.recipients || [defaultRecipient].filter(Boolean)).join(', '));
  const [teamId, setTeamId] = useState(initial?.team_id || '');
  const [throttle, setThrottle] = useState(initial?.throttle_minutes ?? 60);
  const [active, setActive] = useState(initial?.active ?? true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const typeMeta = ALERT_TYPES.find((t) => t.value === alertType);

  // Reset config when type changes (only for NEW alerts; preserve on edit)
  useEffect(() => {
    if (!isEdit) {
      const meta = ALERT_TYPES.find((t) => t.value === alertType);
      setConfig(meta?.defaults || {});
    }
  }, [alertType, isEdit]);

  const save = async () => {
    setSaving(true); setErr(null);
    const emails = recipients.split(',').map((s) => s.trim()).filter(Boolean);
    if (!name) { setErr('Name is required'); setSaving(false); return; }
    if (emails.length === 0) { setErr('At least one recipient is required'); setSaving(false); return; }
    const payload = {
      name, description: description || null,
      alert_type: alertType, config: config || {},
      recipients: emails,
      team_id: teamId || null,
      throttle_minutes: Number(throttle) || 60,
      active,
    };
    try {
      if (isEdit) {
        await axios.put(`${API}/smart-alerts/${initial.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/smart-alerts`, payload, { headers: { Authorization: `Bearer ${token}` } });
      }
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to save alert');
    } finally {
      setSaving(false);
    }
  };

  const renderConfig = () => {
    if (alertType === 'overdue_jobs_count') {
      return (
        <LabeledInput label="Threshold (count)" type="number"
          value={config.threshold ?? 5}
          onChange={(v) => setConfig({ ...config, threshold: Number(v) })}
          testId="cfg-threshold" />
      );
    }
    if (alertType === 'kpi_threshold') {
      return (
        <div className="grid grid-cols-2 gap-3">
          <LabeledSelect label="Metric" value={config.metric || 'completion_rate'}
            onChange={(v) => setConfig({ ...config, metric: v })}
            options={KPI_METRICS} testId="cfg-metric" />
          <LabeledSelect label="Operator" value={config.operator || '<'}
            onChange={(v) => setConfig({ ...config, operator: v })}
            options={[{ value: '<', label: '<' }, { value: '<=', label: '≤' },
                      { value: '>', label: '>' }, { value: '>=', label: '≥' }]}
            testId="cfg-op" />
          <LabeledInput label="Value" type="number" value={config.value ?? 80}
            onChange={(v) => setConfig({ ...config, value: Number(v) })}
            testId="cfg-value" />
          <LabeledSelect label="Window" value={config.date_range_mode || 'this_month'}
            onChange={(v) => setConfig({ ...config, date_range_mode: v })}
            options={[
              { value: 'this_week', label: 'This Week' }, { value: 'last_week', label: 'Last Week' },
              { value: 'this_month', label: 'This Month' }, { value: 'last_month', label: 'Last Month' },
            ]}
            testId="cfg-window" />
        </div>
      );
    }
    if (alertType === 'no_clock_in_by_time' || alertType === 'attendance_absent') {
      return (
        <LabeledInput label="Cutoff Time (UTC, HH:MM)" type="time"
          value={config.cutoff_time || (alertType === 'attendance_absent' ? '11:00' : '09:00')}
          onChange={(v) => setConfig({ ...config, cutoff_time: v })}
          testId="cfg-cutoff" />
      );
    }
    if (alertType === 'job_over_budget') {
      return (
        <LabeledInput label="% of scheduled window" type="number"
          value={config.percent_threshold ?? 80}
          onChange={(v) => setConfig({ ...config, percent_threshold: Number(v) })}
          testId="cfg-pct" />
      );
    }
    return null;
  };

  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center p-4"
         style={{ background: 'rgba(11,19,33,0.85)', backdropFilter: 'blur(4px)' }}
         onClick={onClose}
         data-testid="alert-editor">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-lg rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
           style={{ background: '#1A2433', border: '1px solid #2D3748' }}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-white">
            {isEdit ? 'Edit Smart Alert' : 'New Smart Alert'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><XIcon className="w-5 h-5" /></button>
        </div>

        <div className="space-y-3">
          <LabeledInput label="Name" value={name} onChange={setName} testId="editor-name" />
          <LabeledInput label="Description (optional)" value={description} onChange={setDescription} testId="editor-desc" />
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Alert Type</label>
            <select value={alertType} onChange={(e) => setAlertType(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg text-sm text-white"
                    style={{ background: '#0F1923', border: '1px solid #2D3748' }}
                    data-testid="editor-type">
              {ALERT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            {typeMeta?.description && (
              <p className="text-[11px] mt-1" style={{ color: '#64748B' }}>{typeMeta.description}</p>
            )}
          </div>

          <div className="p-3 rounded-lg" style={{ background: '#0F1923', border: '1px solid #2D3748' }}>
            <p className="text-xs font-semibold mb-2 text-slate-300">Trigger configuration</p>
            {renderConfig()}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Team (optional)</label>
              <select value={teamId} onChange={(e) => setTeamId(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg text-sm text-white"
                      style={{ background: '#0F1923', border: '1px solid #2D3748' }}
                      data-testid="editor-team">
                <option value="">All teams</option>
                {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <LabeledInput label="Throttle (minutes)" type="number"
              value={throttle} onChange={(v) => setThrottle(Number(v))}
              testId="editor-throttle" />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">
              Recipients <span className="text-slate-500">(comma-separated)</span>
            </label>
            <input type="text" value={recipients} onChange={(e) => setRecipients(e.target.value)}
                   className="w-full px-3 py-2 rounded-lg text-sm text-white"
                   style={{ background: '#0F1923', border: '1px solid #2D3748' }}
                   data-testid="editor-recipients" />
          </div>

          <label className="flex items-center gap-2 text-sm text-white">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)}
                   data-testid="editor-active" />
            Active
          </label>

          {err && <p className="text-xs" style={{ color: '#EF4444' }}>{err}</p>}
        </div>

        <div className="flex gap-3 mt-5">
          <button onClick={onClose} disabled={saving}
                  className="flex-1 py-2 rounded-lg font-medium"
                  style={{ background: '#2D3748', color: 'white' }}
                  data-testid="editor-cancel">Cancel</button>
          <button onClick={save} disabled={saving}
                  className="flex-1 py-2 rounded-lg font-medium disabled:opacity-60"
                  style={{ background: '#1E88E5', color: 'white' }}
                  data-testid="editor-save">
            {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Alert')}
          </button>
        </div>
      </div>
    </div>
  );
}

function LabeledInput({ label, value, onChange, type = 'text', testId }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-400 mb-1">{label}</label>
      <input type={type} value={value ?? ''} onChange={(e) => onChange(e.target.value)}
             className="w-full px-3 py-2 rounded-lg text-sm text-white"
             style={{ background: '#0F1923', border: '1px solid #2D3748' }}
             data-testid={testId} />
    </div>
  );
}

function LabeledSelect({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-400 mb-1">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm text-white"
              style={{ background: '#0F1923', border: '1px solid #2D3748' }}
              data-testid={testId}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
